<template>
  <div :class="$style.container">
    <div :class="$style.fieldContainer">
      <div
        v-for="(selectedSite, index) in selectedSites"
        :key="`site${index}`"
        :class="$style.row"
      >
        <div :class="$style.row">
          <b-field :class="$style.field">
            <div class="level margin-bottom8">
              <div class="level-left">
                <label>{{ `Site ${index + 1}` }} </label>
              </div>
              <div class="level-right">
                <b-switch
                  @input="toggled(index, selectedSite.isIdentifierRequired)"
                  :value="selectedSite.isIdentifierRequired"
                >
                  Required
                </b-switch>
                <b-tooltip
                  v-if="selectedSite.isIdentifierRequired"
                  label="Data collection sample code in individual mode is required, please confirm with project administrator."
                  position="is-top"
                  type="is-dark"
                  size="is-large"
                  multilined
                >
                  <b-icon
                    icon="information-outline"
                    size="is-small"
                    custom-size="mdi-16px"
                    type="is-danger"
                  >
                  </b-icon>
                </b-tooltip>
                <b-tooltip
                  v-else
                  label="Data collection sample code in individual mode."
                  position="is-top"
                  type="is-dark"
                >
                  <b-icon
                    icon="information-outline"
                    size="is-small"
                    custom-size="mdi-16px"
                    class="text-color"
                  >
                  </b-icon>
                </b-tooltip>
              </div>
            </div>
            <b-dropdown
              :value="selectedSites[index]"
              @input="(value) => handleSiteChange(index, value)"
              aria-role="list"
              :class="$style.dropdownContainer"
            >
              <button
                class="button"
                :class="$style.dropdownTrigger"
                slot="trigger"
              >
                <span>
                  {{ selectedSite.name || 'Select Site' }}
                </span>
                <b-icon icon="menu-down"></b-icon>
              </button>
              <b-dropdown-item
                aria-role="listitem"
                v-for="(value, index) in siteOptions"
                :value="value"
                :key="index"
              >
                <span>{{ value.name }}</span>
                <p>{{ value.address }}</p>
              </b-dropdown-item>
              <a :class="$style.createNewSiteButton" @click="handleCreate">
                <span>Create New Site</span>
              </a>
            </b-dropdown>
          </b-field>
        </div>

        <div :class="$style.deleteButtonContainer">
          <a
            v-if="selectedSites.length > 1"
            class="is-link"
            @click="handleRemoveSite(index)"
            :class="$style.deleteButton"
          >
            <b-icon icon="delete"></b-icon>
          </a>
        </div>
      </div>

      <a
        class="button is-link is-text-primary is-borderless is-transparent"
        @click="handleAppendSite"
      >
        + Add Site
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { Site } from '@/store/modules/site/types/site.types';
import { RootState } from '@/store/store';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import { CreateProjectParticipantPayload } from '../../../../store/modules/projects/types/projects.types';
import CreateSiteForm from './CreateSiteForm.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import { cloneData } from '@/utils/general.util';

@Component({
  components: {},
})
export default class SelectSite extends Vue {
  @Action('projects/getSites')
  public getSites!: () => void;

  @State((state: RootState) => state.projects.sites)
  public sites!: Site[];

  @State((state: RootState) => state.projects.apiState.getProjectDetail.success)
  public onSuccess!: boolean;

  // to prevent participant selected site been remove
  @Prop()
  public participants!: CreateProjectParticipantPayload[];

  @Prop()
  public value!: Site[];

  public get selectedSites() {
    return this.value;
  }

  public set selectedSites(value) {
    this.$emit('input', value);
  }

  public get siteOptions() {
    const data = this.sites || [];
    return data.filter((site) => {
      return !this.selectedSites.find(({ id }) => id === site.id);
    });
  }

  public toggled(index: number, value: boolean) {
    const selectedSites = cloneData(this.selectedSites);
    selectedSites[index] = {
      ...selectedSites[index],
      isIdentifierRequired: !value,
    };
    this.$emit('input', selectedSites);
  }

  public async mounted() {
    this.getSites();
  }

  public handleSiteChange(index: number, value: Site) {
    if (this.hasAssoicatedSite(index)) {
      return;
    }
    const selectedSites = cloneData(this.selectedSites);
    selectedSites[index] = value;
    this.$emit('input', selectedSites);
  }

  public handleRemoveSite(index: number) {
    if (this.hasAssoicatedSite(index)) {
      return;
    }

    if (!Boolean(this.selectedSites[index].name)) {
      return this.selectedSites.splice(index, 1);
    }

    this.$buefy.modal.open({
      parent: this,
      component: BaseDialog,
      hasModalCard: true,
      trapFocus: true,
      props: {
        title: 'Remove Site',
        content: `You are trying to remove ${this.selectedSites[index].name}. Removing this site will also remove it from corresponding participants.`,
      },
      events: {
        confirm: () => {
          this.selectedSites.splice(index, 1);
        },
      },
    });
  }

  public handleAppendSite() {
    // @ts-ignore
    this.selectedSites.push('');
  }

  public handleCreate() {
    this.$buefy.modal.open({
      parent: this,
      props: {},
      events: {
        submit: (newSite: Site) => {
          // remove replace prepend empty string if create new site one a empty drop down list
          const length = this.selectedSites.length || 0;
          if (length > 0 && !this.selectedSites[length - 1]) {
            this.selectedSites.pop();
            this.selectedSites.push(newSite);
          }
        },
      },
      component: CreateSiteForm,
      hasModalCard: true,
    });
  }

  public hasAssoicatedSite(index: number): boolean {
    // prevent delete or change site that already assigned to participant
    const siteWithParticipant = this.participants.find((participant) => {
      return participant.assignedSiteIds.find(
        (site) => site === this.selectedSites[index].id,
      );
    });
    if (siteWithParticipant) {
      this.$buefy.toast.open({
        type: 'is-danger',
        position: 'is-top',
        message: `Can't change or delete the site that already assigned to participant!`,
      });
    }
    return siteWithParticipant ? true : false;
  }

  @Watch('selectedSites', { immediate: true })
  public onSitesEmpty(value: Site[]): void {
    if (value.length === 0) {
      this.handleAppendSite();
    }
  }
}
</script>

<style lang="scss" module>
.container {
  .fieldContainer {
    padding-top: 0.5rem;
  }

  .row {
    display: flex;
    flex: 1;
    align-items: center;
  }

  .field {
    width: 100%;
    align-items: center;
    margin-bottom: 1.5rem;
    display: block;
  }

  .deleteButtonContainer {
    min-width: 56px;

    .deleteButton {
      padding: 0 1rem;
    }
  }

  .dropdownContainer {
    :global(.dropdown-menu) {
      padding-top: 0;
      z-index: 40;

      :global(.dropdown-content) {
        padding-top: 0;
        padding-bottom: 0;

        a {
          border-bottom: 0.5px solid $grey-border;

          &:last-child {
            padding: 0.5rem;
            border-bottom: none;
          }

          p {
            font-weight: 400;
            color: $grey;
          }
        }
      }
    }

    .dropdownTrigger {
      font-weight: normal !important;
      width: 100%;
      justify-content: space-between;
      padding: 8px 16px;
    }

    .createNewSiteButton {
      display: block;
      width: 100%;
      color: $secondary;
      font-weight: 600;

      &:hover {
        background-color: whitesmoke;
      }
    }
  }

  :global(.b-checkbox.checkbox .control-label) {
    font-weight: normal;
  }
}
</style>
